/**
 * O quadro do Funil (história 3.8) — a carteira de trabalho, de relance (UX-DR4, UX-DR11).
 *
 * Ele é a tela que o Épico 3 existe para entregar: uma coluna por Etapa no computador, cards
 * empilhados no celular, e em cada card quem é a Empresa, quanto vale, quem responde e há quantos
 * dias ninguém fala com ela — este último na cor da régua do FR-12.
 *
 * **O componente não decide nada sobre visibilidade, e essa é a regra inteira.** O serviço
 * devolve os Negócios que a RLS deixa quem está olhando enxergar (FR-30, FR-31): em Vendas, os do
 * próprio Responsável; na Prospecção, todos. Um `filter` aqui por `responsavel_id` seria a matriz
 * do FR-31 escrita num segundo lugar, e os dois divergiriam no primeiro ajuste.
 *
 * **A cor vem pronta do serviço, e não é recalculada aqui.** O AD-10 manda derivá-la na consulta,
 * contra a régua vigente; recalculá-la a partir de `dias_sem_contato` criaria uma segunda régua,
 * que discordaria da primeira no minuto em que a gestão mudasse os cortes entre a resposta e o
 * desenho. O que este arquivo lê de `useRegua` são os **cortes**, e só para escrever a legenda —
 * a mesma `descreverAsFaixas` que o cartão de Administração usa, e é dela que saem também os três
 * rótulos por extenso ("em dia", "esfriando", "abandonado"), para que não existam dois textos
 * para a mesma faixa.
 *
 * **Os dois nomes que o card mostra são resolvidos aqui, com o JWT de quem está olhando** (AD-25).
 * A razão social vem de `useNomesDeEmpresas` (`GET /empresas?ids=`) e o nome do Responsável de
 * `useResponsaveis` — dois serviços diferentes, cada um impondo o seu piso. Resolver isso no
 * servidor do `negocios` entregaria à tela justamente a ficha que a outra policy fecha (FR-30).
 *
 * **E a ausência de um nome tem três causas, que a tela não pode confundir.** O lote ainda está no
 * ar; o lote falhou; ou a policy do `cadastro` não devolveu aquela ficha. Só a terceira é a
 * degradação documentada — "existe, e a ficha não é sua", e aí o título vira o **código** do
 * Negócio (AD-12). As outras duas são transitórias ou defeito, e **cada uma tem a sua marca**: o
 * card sai com `data-nome="pendente"` enquanto espera (com `aria-busy` e o aviso ao leitor de
 * tela), com `data-nome="indisponivel"` quando o lote falhou, e o quadro ainda escreve uma recusa
 * própria por cima. Só `data-nome="ausente"` afirma o FR-30. Sem essa distinção, um
 * `GET /empresas` recusado faria a tela afirmar, sobre a carteira inteira, que nenhuma daquelas
 * fichas é de quem está olhando — e o atributo, que é por onde a suíte observa a diferença, diria
 * a mesma coisa nos dois casos.
 *
 * **Uma consulta por tela, e nunca uma por card.** São cinco idas à rede no total — o catálogo, o
 * quadro, a régua, os nomes em lote e a equipe —, e os vocabulários viram `Map` por `useMemo`. É o
 * mesmo desenho de `ListaDeEmpresas`. Duas delas são em **cascata**: os nomes só partem depois de
 * o quadro responder, porque é dele que saem os `empresa_id`.
 *
 * **Duas árvores, escolhidas por `useEhCelular()` — e não um `@media`.** No computador o quadro é
 * uma grade de colunas; no celular, uma lista única em que a Etapa vira **tag**. Toda informação
 * que existe a 1280 px continua alcançável a 375 px (NFR-1); o que muda é o arranjo.
 *
 * **O ponto de previsão (11.8, FR-78)** entra no card só quando o Negócio tem previsão marcada: o
 * ponto na cor pastel do item, com o rótulo por extenso ao lado (cor nunca é o único sinal) — no
 * computador e no card empilhado do celular. Sem previsão, nada (nem ponto neutro). O card traz só
 * o `id` do item; rótulo e cor vêm do catálogo do `negocios` (`useItensDePrevisao`, AD-25), que só
 * é pedido quando algum card tem previsão.
 *
 * **O que este componente não faz**, e a ausência é o recorte: não edita, não arrasta, não ganha
 * nem perde e não exclui — o card é **leitura**. Mover, o desfecho e a exclusão acontecem na
 * **ficha** (3.10), e desde ela o card abre: cada um é um `<Link>` para `/negocios/{id}`, no
 * computador e no celular (DW-156). Um `div` com `onClick` chegaria à metade disso — sem "abrir em
 * nova aba", sem foco por teclado e sem o anúncio de "link" no leitor de tela.
 *
 * **Os textos e os formatadores moram em `apresentacao.ts`, e não aqui.** "sem orçamento", "valor
 * não visível para SDR", "sem próximo passo", o `Intl.NumberFormat` do dinheiro e o rótulo por
 * extenso de cada cor são os **mesmos** desta tela e do painel do dia (3.9). Duplicá-los faria a
 * frase mudar num lugar e ficar antiga no outro, com as duas suítes verdes — e é justamente isso
 * que os parágrafos acima afirmam que não acontece.
 *
 * **"Próximo passo" nasce fixo, e o motivo tem nome.** Próximo passo é Tarefa, e não existe
 * serviço de Tarefa: o Épico 4 o cria. Inventar um campo de API sem fonte congelaria o contrato
 * daquela história; deixar o slot fora do card obrigaria a refazer o layout depois. O protótipo já
 * desenha o estado vazio em âmbar (`.dc.html:1134-1135`), e é ele que entra.
 */

import {
  type PointerEvent as EventoDePonteiro,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useNomesDeEmpresas, useNomesDePessoas } from '@/modulos/cadastro/api'
import { type EscopoDeCarteira, useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { descreverAsFaixas } from '@/modulos/negocios/ReguaDeDias'
import {
  type Etapa,
  type Funil,
  type NegocioNoQuadro,
  useItensDePrevisao,
  useMoverCardDeEtapa,
  useNegociosDoFunil,
  useRegua,
} from '@/modulos/negocios/api'
import {
  MarcaDePrevisaoNoCard,
  type PrevisaoNoCard,
  previsaoNoCard,
} from '@/modulos/negocios/MarcaDePrevisao'
import {
  CONTATO_A_CAMINHO,
  DEFINA_A_EMPRESA_PARA_MOVER,
  EMPRESA_A_DEFINIR,
  MOEDA,
  NUMERO,
  ORCAMENTO_SO_DEPOIS_DA_EMPRESA,
  SEM_ORCAMENTO,
  SEM_PROXIMO_PASSO,
  sinalDaCor,
} from '@/modulos/negocios/apresentacao'
import { IconeDeCopiar, copiarParaAreaDeTransferencia } from '@/modulos/negocios/copiar'
import { LinkDoNegocio, useAbrirNegocio } from '@/modulos/negocios/navegacao-do-negocio'
import { ehDeGestao } from '@/shell/navegacao'
import { useEhCelular } from '@/shell/Shell'
import './quadro-do-funil.css'

const ID_DO_TITULO = 'quadro-do-funil-titulo'
const NOME_DO_QUADRO = 'Quadro por etapa'

/**
 * O nome acessível da lista única do celular.
 *
 * Ele é **diferente** de `NOME_DO_QUADRO` de propósito: a lista mora dentro da região que já se
 * chama "Quadro por etapa", e repetir o nome faria o leitor de tela anunciar o mesmo rótulo duas
 * vezes, aninhado. No computador o problema não existe — cada coluna nomeia a sua lista com a
 * Etapa (`Negócios em ...`) —, e esta constante é o equivalente para a árvore que não tem coluna.
 */
const NOME_DA_LISTA_DO_CELULAR = 'Negócios em andamento'
const ROTULO_DA_PILULA = 'Funil aberto'
const CARREGANDO = 'Carregando o quadro…'
const SEM_NEGOCIOS = 'Nenhum negócio em andamento neste funil.'
const SEM_ETAPAS = 'Este funil ainda não tem etapas.'
const SEM_FUNIS = 'Nenhum funil cadastrado. A gestão cria o primeiro na Administração.'
const TENTAR_DE_NOVO = 'Tentar de novo'

/** A ação primária do Funil, e só dele (11.4). */
export const ROTULO_DO_NOVO_NEGOCIO = '+ Negócio'

/**
 * A recusa do catálogo, que é pré-condição do quadro inteiro.
 *
 * Ela é uma frase própria, e não a mensagem do serviço repetida: quem falhou foi `GET /funis`, e
 * quem está olhando precisa saber que o que faltou foram **as colunas** — sem elas não há Funil
 * escolhido, e a leitura do quadro nem chega a sair.
 */
const SEM_CATALOGO =
  'Não foi possível carregar os funis e as etapas. Sem eles não há quadro para desenhar.'

/**
 * O aviso de que os nomes das Empresas não vieram — e que o código no título **não** quer dizer
 * "esta ficha não é sua".
 *
 * Ele existe porque a degradação silenciosa é indistinguível do recorte de visibilidade: um card com
 * o código é uma coisa legítima e rara (FR-30); uma tela inteira deles, por causa de um `GET
 * /empresas` recusado, é defeito — e sem esta frase a operação leria o defeito como regra.
 */
const NOMES_NAO_VIERAM =
  'Não foi possível carregar os nomes das empresas; os cards mostram o código do negócio.'

/** O que o leitor de tela ouve enquanto o nome da Empresa ainda está no ar. */
const NOME_A_CAMINHO = ' (carregando o nome da empresa)'
/** A dica de arrastar, ao lado da frase de escopo (protótipo v2) — só no computador. */
const DICA_DE_ARRASTO = 'arraste o card para mudar de etapa'
/** Os filtros do quadro (protótipo v2) — recorte de leitura sobre os cards já lidos. */
export const ROTULO_DOS_FILTROS = 'Filtros'
export const ROTULO_DE_OCULTAR_FILTROS = 'Ocultar filtros'
const NOME_DOS_FILTROS = 'Filtros do quadro'
const LIMPAR_FILTROS = 'Limpar'
export const FILTROS_SEM_RESULTADO = 'Nenhum negócio corresponde à busca e aos filtros.'
/** O toast do Copiar do card — o mesmo texto do cartão de ID do modal (11.4). */
export const ID_COPIADO_DO_CARD = 'ID copiado.'
export const COPIA_DO_CARD_FALHOU = 'Não foi possível copiar. Abra o negócio e copie o ID de lá.'
/**
 * 13.20b2 (herança da 13.20b1): o toast do Copiar no card do Negócio sem Empresa avisa que o
 * orçamento lançado com esse código só entra depois de definir a Empresa.
 */
export const ID_COPIADO_SEM_EMPRESA = `${ID_COPIADO_DO_CARD} ${ORCAMENTO_SO_DEPOIS_DA_EMPRESA}`
const DURACAO_DO_TOAST_EM_MS = 2500

/** O que a pessoa pode recortar no quadro. Vazio é "todos". */
interface FiltrosDoQuadro {
  responsavel: string
  etapa: string
  cor: '' | NegocioNoQuadro['cor']
  orcamento: '' | 'com' | 'sem'
}
const SEM_FILTROS: FiltrosDoQuadro = { responsavel: '', etapa: '', cor: '', orcamento: '' }

/** Quantos filtros estão ativos — é o número ao lado de "Filtros". */
function filtrosAtivos(filtros: FiltrosDoQuadro): number {
  return Object.values(filtros).filter((valor) => valor !== '').length
}

/** O card passa pela busca e pelos filtros? (tudo sobre o que o card já carrega) */
function passaNoRecorte(card: CardDoQuadro, termo: string, filtros: FiltrosDoQuadro): boolean {
  if (
    termo !== '' &&
    !card.titulo.toLowerCase().includes(termo) &&
    !(card.razaoSocial?.toLowerCase().includes(termo) ?? false) &&
    !String(card.codigo).includes(termo)
  ) {
    return false
  }
  if (filtros.responsavel !== '' && card.responsavelId !== filtros.responsavel) {
    return false
  }
  if (filtros.etapa !== '' && card.etapaId !== filtros.etapa) {
    return false
  }
  if (filtros.cor !== '' && card.cor !== filtros.cor) {
    return false
  }
  if (filtros.orcamento === 'com' && card.orcamento === null) {
    return false
  }
  if (filtros.orcamento === 'sem' && card.orcamento !== null) {
    return false
  }
  return true
}

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * Por que o título do card não é a razão social — as **quatro** causas, separadas.
 *
 * `resolvido` é o caso comum. `pendente` é transitório (o lote está no ar, e a cascata quadro →
 * nomes garante que essa janela existe). `indisponivel` é o lote que **falhou**: a resposta não
 * chegou, e nada se sabe sobre aquela ficha. `ausente` é a única das quatro que afirma alguma
 * coisa, e é a degradação documentada do FR-30: o lote chegou inteiro e a policy do `cadastro`
 * não devolveu aquela ficha.
 *
 * A quarta existe porque `NomesDeEmpresas` separa `carregando` de `falhou` justamente para que
 * quem chama não colapse as duas em "não é sua" — e colapsá-las aqui seria desfazer, no último
 * passo, a separação que a camada de baixo carregou até aqui.
 */
type OrigemDoTitulo = 'resolvido' | 'pendente' | 'indisponivel' | 'ausente'

/**
 * O que a tela desenha por card — o view-model, montado uma vez para as duas árvores.
 *
 * Ele existe para que computador e celular escrevam **o mesmo texto**: dois cálculos paralelos
 * seriam duas chances de o celular mostrar "sem orçamento" onde o computador mostra um número, e
 * a paridade da NFR-1 viraria coincidência.
 */
interface CardDoQuadro {
  id: string
  /** O código humano do Negócio (AD-12) — o que se digita no "Exportar" do Carga Máquina. */
  codigo: number
  /**
   * O nome da Empresa (13.7: o nome exibido — o nome fantasia, e a razão social sem ele); o código
   * do Negócio quando ele não veio (por qualquer das outras causas).
   */
  titulo: string
  /** A razão social (13.7), só para a busca do quadro achar a Empresa por ela também. */
  razaoSocial?: string
  /** Qual das quatro causas produziu o título acima. */
  origemDoTitulo: OrigemDoTitulo
  /**
   * O Negócio sem Empresa (13.20b2): o título é o nome do contato (ou o código, sem ele visível),
   * a linha "Empresa a definir" entra no card e ele **não arrasta** — o serviço recusa o mover.
   */
  semEmpresa: boolean
  /** O nome do Responsável, ou o traço quando o cache da equipe não o conhece. */
  responsavel: string
  /** O `id` do Responsável — é por ele que o filtro de Responsável recorta. */
  responsavelId: string
  /**
   * O texto do dinheiro, ou `null` no recorte da SDR (FR-62): aí o valor **não é desenhado** —
   * nem traço, nem frase —, como no modal do Negócio (State Patterns, 11.4).
   */
  valor: string | null
  /**
   * O orçamento como número, ou `null` quando não há (ou quando não é da conta de quem olha).
   *
   * Ele viaja no card porque a soma da coluna sai daqui: um segundo agrupamento sobre os itens
   * crus, só para somar, era a mesma travessia feita duas vezes — e a prop que o carregava se
   * chamava `cardsPorEtapa` carregando item cru, que é a armadilha de nome que isto desfaz.
   *
   * **`Number()` sobre o texto de duas casas**, e por isso a soma é de ponto flutuante: ela é o
   * resumo de relance da coluna, e não um total contábil. Quem soma dinheiro para valer lê o
   * evento (Épico 7).
   */
  orcamento: number | null
  dias: string
  /** A frase por extenso que acompanha o ponto colorido — a mesma informação, sem depender de cor. */
  sinal: string
  cor: NegocioNoQuadro['cor']
  etapaId: string
  /**
   * A Previsão de fechamento marcada (11.8), já rotulada pelo catálogo; `'indisponivel'` quando a
   * leitura do rótulo falhou (o card diz isso, discreto — falha não vira "sem previsão"); `null` sem
   * previsão, ou com o rótulo ainda a caminho: aí o card não desenha ponto nenhum.
   */
  previsao: PrevisaoNoCard
}

/** O que uma célula mostra quando o valor não existe ou não pôde ser resolvido. */
const VAZIO = '—'

/**
 * A frase de escopo da barra — o que aquele quadro está mostrando, e por quê (protótipo `:1118`).
 *
 * Ela existe porque duas pessoas olhando a mesma tela veem coisas diferentes, e sem a frase a
 * diferença pareceria defeito: o Vendedor que abre Vendas e vê três cards precisa saber que os
 * outros existem e não são dele. O nome vem do **Funil**, e não de um literal: a gestão o
 * renomeia quando quiser (FR-1, FR-63). Desde a 13.11c o tipo não muda a frase: em todo Funil "é
 * responsável, vê" (a Prospecção fechou, FR-31 emendado), e quem distingue as duas é o escopo.
 *
 * **Nenhuma das duas frases concorda em gênero com o nome.** As duas seguem o mesmo molde
 * `<nome>: <o que a tela mostra>`, porque o nome é dado da gestão: renomear o Funil para "Funil de
 * entrada" não pode produzir texto errado numa tela que ninguém foi avisado de olhar.
 */
function escopoDo(funil: Funil, escopo: EscopoDeCarteira | undefined): string {
  // 13.11c (FR-31 emendado): a Prospecção fechou, e o tipo não muda mais a frase — o escopo muda.
  return escopo !== undefined && ehDeGestao(escopo)
    ? `${funil.nome}: todas as carteiras`
    : `${funil.nome}: só o que está sob sua responsabilidade`
}

/**
 * O resumo do cabeçalho da coluna: a contagem sempre, e a soma em BRL só para quem vê dinheiro
 * (protótipo `:1125`).
 *
 * **A soma é a dos cards visíveis**, e portanto pós-policy e sem Negócio fechado: ela descreve o
 * que está na coluna, e não o que existe no banco. Um total que somasse mais do que a tela mostra
 * seria a tela se contradizendo — e vazaria, por subtração, o dinheiro que a policy escondeu.
 *
 * **A presença da soma é decidida pela presença de orçamento, e não pelo total ser diferente de
 * zero.** Uma coluna em que todo card vale `R$ 0` tem um total verdadeiro — zero —, e omiti-lo
 * faria o cabeçalho calar justamente sobre o que os cards estão gritando. Quem não tem orçamento
 * nenhum é que não tem soma a mostrar.
 */
function resumoDaColuna(cards: readonly CardDoQuadro[], mostraDinheiro: boolean): string {
  const contagem = NUMERO.format(cards.length)
  if (!mostraDinheiro) {
    return contagem
  }
  const comOrcamento = cards.filter((card) => card.orcamento !== null)
  if (comOrcamento.length === 0) {
    return contagem
  }
  const total = comOrcamento.reduce((soma, card) => soma + (card.orcamento ?? 0), 0)
  return `${contagem} · ${MOEDA.format(total)}`
}

/**
 * O ponto colorido com o texto ao lado — o padrão de acessibilidade da régua, repetido no card.
 *
 * O ponto é `aria-hidden` porque ele é **decorativo**: quem não distingue as cores continua lendo
 * a mesma informação por escrito, no elemento ao lado. Cor sozinha nunca é o único portador de
 * significado nesta tela.
 */
function PontoDaCor({ cor }: { cor: NegocioNoQuadro['cor'] }) {
  return <span className={`quadro__ponto quadro__ponto--${cor}`} aria-hidden="true" />
}

/** A marca da Previsão no card (11.8): o ponto com o rótulo, o aviso de falha, ou nada. */
function PrevisaoDoCard({ previsao }: { previsao: CardDoQuadro['previsao'] }) {
  return (
    <MarcaDePrevisaoNoCard
      previsao={previsao}
      classe="quadro__previsao"
      classeIndisponivel="quadro__previsao-indisponivel"
    />
  )
}

/** O título do card, com o aviso de "ainda estou esperando o nome" quando for o caso. */
function TituloDoCard({ card, classe }: { card: CardDoQuadro; classe: string }) {
  const pendente = card.origemDoTitulo === 'pendente'
  return (
    <span className={classe} data-nome={card.origemDoTitulo} aria-busy={pendente || undefined}>
      {card.titulo}
      {pendente ? (
        <span className="quadro__so-para-leitor">
          {card.semEmpresa ? CONTATO_A_CAMINHO : NOME_A_CAMINHO}
        </span>
      ) : null}
    </span>
  )
}

/** A linha "Empresa a definir" do card do Negócio sem Empresa (13.20b2), nas duas árvores. */
function EmpresaADefinir({ card }: { card: CardDoQuadro }) {
  return card.semEmpresa ? <span className="quadro__sem-empresa">{EMPRESA_A_DEFINIR}</span> : null
}

/**
 * O arrastar do card em andamento (11.4, desktop). Guardado em estado para desenhar o card
 * deslocado e a coluna-alvo; o `ref` espelhado é o que os ouvintes da janela leem.
 */
interface Arrasto {
  negocioId: string
  origem: string
  x0: number
  y0: number
  dx: number
  dy: number
  /** Passou do limiar de movimento — antes disso, é um clique e nada se arrasta. */
  ativo: boolean
  /** A Etapa sob o ponteiro, ou `null` fora de toda coluna. */
  alvo: string | null
}

/** Quantos pixels o ponteiro precisa andar para o gesto virar arrasto (o clique continua clique). */
const LIMIAR_DO_ARRASTO = 5

/**
 * Um card do quadro. **Só o nome é link** (Interaction Primitives: "clique no nome abre"): ele
 * abre o Negócio em modal sobre o Funil. O resto do card é a alça do arrastar no computador.
 */
function Card({
  card,
  arrastando,
  deslocamento,
  aoApertar,
  aoCopiar,
  aoAbrir,
}: {
  card: CardDoQuadro
  arrastando: boolean
  deslocamento: { dx: number; dy: number } | null
  aoApertar?: (card: CardDoQuadro, evento: EventoDePonteiro<HTMLLIElement>) => void
  aoCopiar: (codigo: number, semEmpresa: boolean) => void
  aoAbrir?: (negocioId: string) => void
}) {
  /* 13.20b2: o Negócio sem Empresa não arrasta (o serviço recusaria o mover); a razão fica no
     `title` do card e, para o leitor de tela, ao lado da linha "Empresa a definir". Só o arrastar
     é desligado (code review, B8): o clique no corpo abre o Negócio, como nos outros cards (o
     nome e o "Copiar" têm gesto próprio). */
  const apertar = card.semEmpresa ? undefined : aoApertar
  const abrirPeloCorpo =
    card.semEmpresa && aoAbrir !== undefined
      ? (evento: { target: EventTarget }) => {
          if ((evento.target as Element).closest('a, button') !== null) {
            return
          }
          aoAbrir(card.id)
        }
      : undefined
  return (
    <li
      className="quadro__card"
      data-cor={card.cor}
      data-sem-empresa={card.semEmpresa || undefined}
      title={card.semEmpresa ? DEFINA_A_EMPRESA_PARA_MOVER : undefined}
      data-arrastavel={apertar === undefined ? undefined : 'true'}
      data-arrastando={arrastando || undefined}
      style={
        deslocamento === null
          ? undefined
          : { transform: `translate(${String(deslocamento.dx)}px, ${String(deslocamento.dy)}px)` }
      }
      onPointerDown={
        apertar === undefined
          ? undefined
          : (evento) => {
              apertar(card, evento)
            }
      }
      onClick={abrirPeloCorpo}
    >
      <div className="quadro__card-conteudo">
        <div className="quadro__card-topo">
          <LinkDoNegocio
            negocioId={card.id}
            className="quadro__card-nome"
            aria-label={`Abrir ${card.titulo}`}
          >
            <TituloDoCard card={card} classe="quadro__card-titulo" />
          </LinkDoNegocio>
          {/* O ID mono pequeno com Copiar (DESIGN.md, `card-de-negocio`; protótipo v2 `.card__id`):
              a ação rápida de quem vai digitar o código no "Exportar" do Carga Máquina. O
              `pointerdown` para aqui para o clique não virar o começo de um arrasto. */}
          <span className="quadro__card-id numero">
            <span>{card.codigo}</span>
            <button
              type="button"
              className="quadro__copiar"
              title="Copiar ID"
              aria-label={`Copiar o ID ${String(card.codigo)}`}
              onPointerDown={(evento) => {
                evento.stopPropagation()
              }}
              onClick={() => {
                aoCopiar(card.codigo, card.semEmpresa)
              }}
            >
              <IconeDeCopiar />
            </button>
          </span>
        </div>
        <EmpresaADefinir card={card} />
        {card.semEmpresa ? (
          <span className="quadro__so-para-leitor">{` · ${DEFINA_A_EMPRESA_PARA_MOVER}`}</span>
        ) : null}
        {card.valor === null ? null : <span className="quadro__valor numero">{card.valor}</span>}
        <PrevisaoDoCard previsao={card.previsao} />
        <div className="quadro__card-rodape">
          <span className="quadro__responsavel">{card.responsavel}</span>
          {/* Os dias e a cor andam juntos, e o texto ao lado do ponto é o que os explica sem
              depender de enxergar a cor. */}
          <span className="quadro__dias numero" data-cor={card.cor}>
            {card.dias}
            <span className="quadro__so-para-leitor">{` sem contato · ${card.sinal}`}</span>
          </span>
        </div>
        <div className="quadro__proximo">
          <span className="quadro__proximo-texto">{SEM_PROXIMO_PASSO}</span>
        </div>
      </div>
    </li>
  )
}

/** A cor pastel da Etapa como atributo de estilo — `nenhuma` é o neutro do quadro. */
function corDaEtapa(cor: Etapa['cor'] | undefined): string {
  return cor ?? 'nenhuma'
}

function Coluna({
  nome,
  etapaId,
  cor,
  cards,
  mostraDinheiro,
  arrasto,
  aoApertar,
  aoCopiar,
  aoAbrir,
}: {
  nome: string
  etapaId?: string
  cor?: Etapa['cor']
  cards: readonly CardDoQuadro[]
  mostraDinheiro: boolean
  arrasto: Arrasto | null
  aoApertar?: (card: CardDoQuadro, evento: EventoDePonteiro<HTMLLIElement>) => void
  aoCopiar: (codigo: number, semEmpresa: boolean) => void
  /** O clique no corpo do card que não arrasta (o do Negócio sem Empresa) abre o Negócio. */
  aoAbrir?: (negocioId: string) => void
}) {
  const alvo =
    arrasto !== null && arrasto.ativo && etapaId !== undefined && arrasto.alvo === etapaId
  return (
    <section
      className="quadro__coluna"
      aria-label={`Etapa ${nome}`}
      data-etapa-id={etapaId}
      data-cor-da-etapa={corDaEtapa(cor)}
      data-alvo={alvo && arrasto.origem !== etapaId ? 'true' : undefined}
    >
      <div className="quadro__coluna-cabecalho">
        <span className="quadro__coluna-nome">{nome}</span>
        <span className="quadro__coluna-resumo numero">
          {resumoDaColuna(cards, mostraDinheiro)}
        </span>
      </div>
      <ul className="quadro__cards" aria-label={`Negócios em ${nome}`}>
        {cards.map((card) => {
          const esteArrasta = arrasto !== null && arrasto.ativo && arrasto.negocioId === card.id
          return (
            <Card
              card={card}
              key={card.id}
              arrastando={esteArrasta}
              deslocamento={esteArrasta ? { dx: arrasto.dx, dy: arrasto.dy } : null}
              aoApertar={aoApertar}
              aoCopiar={aoCopiar}
              aoAbrir={aoAbrir}
            />
          )
        })}
      </ul>
    </section>
  )
}

/**
 * A coluna dos Negócios cuja Etapa o catálogo não conhece.
 *
 * Um Negócio só existe numa Etapa (FK), e o catálogo e o quadro vêm do mesmo serviço — mas são
 * duas leituras, e entre elas a gestão pode ter criado uma Etapa nova. Sumir com o card seria
 * esconder trabalho. Ela não recebe card arrastado: não há Etapa para onde mover.
 */
const COLUNA_DOS_ORFAOS = 'Fora das etapas conhecidas'

/**
 * As colunas do computador — e o **arrastar** entre elas (11.4), em Pointer Events próprios, sem
 * biblioteca (a spec o manda; AD-29 não cobre drag-and-drop). O gesto: apertar o card (fora do
 * nome), andar além do limiar, soltar sobre outra coluna. A coluna-alvo é achada pelo retângulo de
 * cada `[data-etapa-id]`. Soltar fora ou na mesma coluna não faz nada. A troca é otimista
 * (`useMoverCardDeEtapa`): o card muda de coluna sem recarregar o quadro, e volta se o serviço
 * recusar — a recusa sai como veio, acima do quadro.
 */
function Colunas({
  etapas,
  porEtapa,
  orfaos,
  mostraDinheiro,
  aoSoltar,
  aoCopiar,
  aoAbrir,
}: {
  etapas: readonly Etapa[]
  porEtapa: ReadonlyMap<string, CardDoQuadro[]>
  orfaos: readonly CardDoQuadro[]
  mostraDinheiro: boolean
  aoSoltar: (negocioId: string, etapaId: string) => void
  aoCopiar: (codigo: number, semEmpresa: boolean) => void
  /** O clique no card — apertar e soltar sem arrastar — abre o Negócio (protótipo v2). */
  aoAbrir: (negocioId: string) => void
}) {
  const quadro = useRef<HTMLDivElement>(null)
  const [arrasto, setArrasto] = useState<Arrasto | null>(null)
  /* O gesto vive no `ref` (os ouvintes da janela o leem na hora); o estado, que só desenha, é
     atualizado **uma vez por quadro de pintura** (`requestAnimationFrame`) — um `pointermove` por
     pixel não vira um render por pixel. */
  const atual = useRef<Arrasto | null>(null)
  const pintura = useRef<number | null>(null)
  const desligar = useRef<(() => void) | null>(null)
  const soltarNoDestino = useRef(aoSoltar)
  useEffect(() => {
    soltarNoDestino.current = aoSoltar
  }, [aoSoltar])
  const abrirONegocio = useRef(aoAbrir)
  useEffect(() => {
    abrirONegocio.current = aoAbrir
  }, [aoAbrir])

  const pintar = useCallback(() => {
    if (pintura.current !== null) {
      return
    }
    pintura.current = window.requestAnimationFrame(() => {
      pintura.current = null
      setArrasto(atual.current)
    })
  }, [])

  const encerrar = useCallback(() => {
    desligar.current?.()
    desligar.current = null
    atual.current = null
    if (pintura.current !== null) {
      window.cancelAnimationFrame(pintura.current)
      pintura.current = null
    }
    setArrasto(null)
  }, [])

  // Desmontado no meio de um gesto (troca de Funil, de largura), nada fica pendurado na janela.
  useEffect(
    () => () => {
      desligar.current?.()
      if (pintura.current !== null) {
        window.cancelAnimationFrame(pintura.current)
      }
    },
    [],
  )

  const alvoEm = useCallback((x: number, y: number): string | null => {
    const colunas = quadro.current?.querySelectorAll<HTMLElement>('[data-etapa-id]') ?? []
    for (const coluna of colunas) {
      const caixa = coluna.getBoundingClientRect()
      if (x >= caixa.left && x <= caixa.right && y >= caixa.top && y <= caixa.bottom) {
        return coluna.dataset.etapaId ?? null
      }
    }
    return null
  }, [])

  const apertar = useCallback(
    (card: CardDoQuadro, evento: EventoDePonteiro<HTMLLIElement>) => {
      // Só o botão principal, e nunca a partir do nome: o nome é o alvo do clique que abre.
      /* O link do nome e o botão de copiar têm gesto próprio: neles o card não começa nada. */
      if (evento.button !== 0 || (evento.target as Element).closest('a, button') !== null) {
        return
      }
      /* A captura mantém o gesto com este card mesmo quando o ponteiro sai dele (ou da janela);
         alguns ambientes não a oferecem, e o gesto continua pelos ouvintes da janela. */
      try {
        evento.currentTarget.setPointerCapture(evento.pointerId)
      } catch {
        // sem captura: o gesto segue igual
      }
      desligar.current?.()
      atual.current = {
        negocioId: card.id,
        origem: card.etapaId,
        x0: evento.clientX,
        y0: evento.clientY,
        dx: 0,
        dy: 0,
        ativo: false,
        alvo: null,
      }
      pintar()

      const aoMover = (movimento: PointerEvent) => {
        const agora = atual.current
        if (agora === null) {
          return
        }
        const dx = movimento.clientX - agora.x0
        const dy = movimento.clientY - agora.y0
        const ativo = agora.ativo || Math.hypot(dx, dy) > LIMIAR_DO_ARRASTO
        atual.current = {
          ...agora,
          dx,
          dy,
          ativo,
          alvo: ativo ? alvoEm(movimento.clientX, movimento.clientY) : null,
        }
        pintar()
      }
      const aoSoltarOPonteiro = (soltura: PointerEvent) => {
        const agora = atual.current
        encerrar()
        if (agora === null) {
          return
        }
        /* Apertou e soltou sem passar do limiar: é clique, e clique abre o Negócio (protótipo v2:
           o card inteiro abre; arrastar é apertar, segurar e mover). */
        if (!agora.ativo) {
          abrirONegocio.current(agora.negocioId)
          return
        }
        const destino = alvoEm(soltura.clientX, soltura.clientY)
        if (destino !== null && destino !== agora.origem) {
          soltarNoDestino.current(agora.negocioId, destino)
        }
      }
      /* Cancelar: o sistema tomou o ponteiro, ou a janela perdeu o foco (troca de aba, alerta do
         sistema) — o `pointerup` pode nunca chegar, e o card não pode ficar grudado no cursor. */
      const aoCancelar = () => {
        encerrar()
      }
      window.addEventListener('pointermove', aoMover)
      window.addEventListener('pointerup', aoSoltarOPonteiro)
      window.addEventListener('pointercancel', aoCancelar)
      window.addEventListener('blur', aoCancelar)
      desligar.current = () => {
        window.removeEventListener('pointermove', aoMover)
        window.removeEventListener('pointerup', aoSoltarOPonteiro)
        window.removeEventListener('pointercancel', aoCancelar)
        window.removeEventListener('blur', aoCancelar)
      }
    },
    [alvoEm, encerrar, pintar],
  )

  return (
    <div
      className="quadro__colunas"
      ref={quadro}
      data-arrastando={arrasto?.ativo === true ? 'true' : undefined}
    >
      {etapas.map((etapa) => (
        <Coluna
          key={etapa.id}
          nome={etapa.nome}
          etapaId={etapa.id}
          cor={etapa.cor}
          cards={porEtapa.get(etapa.id) ?? []}
          mostraDinheiro={mostraDinheiro}
          arrasto={arrasto}
          aoApertar={apertar}
          aoCopiar={aoCopiar}
          aoAbrir={aoAbrir}
        />
      ))}
      {orfaos.length === 0 ? null : (
        <Coluna
          nome={COLUNA_DOS_ORFAOS}
          cards={orfaos}
          mostraDinheiro={mostraDinheiro}
          arrasto={null}
          aoCopiar={aoCopiar}
        />
      )}
    </div>
  )
}

/**
 * A lista do celular: cards empilhados com a Etapa como tag, pintada com a cor da Etapa. **Sem
 * arrastar** — no celular, mover é pelo chevron do modal (11.4). Só o nome é link.
 */
function CardsEmpilhados({
  cards,
  etapaDe,
}: {
  cards: readonly CardDoQuadro[]
  etapaDe: (etapaId: string) => { nome: string; cor: Etapa['cor'] | undefined }
}) {
  return (
    <ul className="quadro__empilhados" aria-label={NOME_DA_LISTA_DO_CELULAR}>
      {cards.map((card) => {
        const etapa = etapaDe(card.etapaId)
        return (
          <li
            className="quadro__empilhado"
            key={card.id}
            data-cor={card.cor}
            data-sem-empresa={card.semEmpresa || undefined}
          >
            <div className="quadro__card-conteudo">
              <div className="quadro__card-topo">
                <LinkDoNegocio
                  negocioId={card.id}
                  className="quadro__card-nome"
                  aria-label={`Abrir ${card.titulo}`}
                >
                  <TituloDoCard card={card} classe="quadro__empilhado-titulo" />
                </LinkDoNegocio>
                <span className="quadro__tag" data-cor-da-etapa={corDaEtapa(etapa.cor)}>
                  {etapa.nome}
                </span>
              </div>
              <EmpresaADefinir card={card} />
              <span className="quadro__empilhado-resumo">
                {[card.valor, `${card.dias} sem contato`, SEM_PROXIMO_PASSO]
                  .filter((parte) => parte !== null)
                  .join(' · ')}
              </span>
              {/* O ponto com o rótulo ao lado também no celular: cor nunca é o único sinal. */}
              <PrevisaoDoCard previsao={card.previsao} />
              <span className="quadro__empilhado-responsavel">
                {card.responsavel}
                <span className="quadro__so-para-leitor">{` · ${card.sinal}`}</span>
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export interface PropsDoQuadroDoFunil {
  /** Os Funis do catálogo, na ordem do serviço. Vazio enquanto `GET /funis` não respondeu. */
  funis: readonly Funil[]
  /** O Funil aberto — o da URL, ou o padrão do perfil. `undefined` até o catálogo chegar. */
  funil: Funil | undefined
  /**
   * A recusa de `GET /funis`, quando houve uma.
   *
   * Ela precisa **chegar aqui**: sem o catálogo não há Funil escolhido, a consulta do quadro nem
   * sai (`enabled`), e `consulta.data` fica `undefined` para sempre. Sem esta prop, a fórmula de
   * `carregando` leria isso como "ainda está vindo" e a tela ficaria em "Carregando o quadro…"
   * eternamente, sem erro e sem saída — o modo de falha que `ListaDeEmpresas` já tinha resolvido
   * com o `!isError` na frente da fórmula.
   */
  erroDoCatalogo?: unknown
  /** Relê o catálogo. É a saída que a recusa de `GET /funis` precisa oferecer. */
  aoRecarregarOCatalogo?: () => void
  /**
   * Se `GET /funis` ainda está no ar.
   *
   * Ela existe pelo mesmo motivo de `erroDoCatalogo`, e para o **outro** desfecho sem recusa: o
   * `200` com lista vazia. Uma instalação que ficou sem Funil nenhum — a gestão excluiu os dois —
   * chega aqui com `funis: []`, `funil: undefined` e erro nenhum, e a fórmula de `carregando`
   * leria isso como "o catálogo ainda vem". `funis.length === 0` sozinho não distingue: é também
   * o estado do primeiro quadro de renderização, antes da resposta. Só o par
   * `vazio + já respondeu` é catálogo vazio de verdade.
   */
  catalogoCarregando?: boolean
  /** Escreve o Funil novo na URL. O quadro nunca guarda o "onde eu estou" por conta própria. */
  aoTrocarDeFunil: (funilId: string) => void
  /**
   * Abre a criação de Negócio (11.4). No Funil vazio o `+ Negócio` aparece no centro do quadro
   * (State Patterns: vazio de primeira vez); sem a prop, o quadro vazio só diz que está vazio.
   */
  aoCriarNegocio?: () => void
  /** O texto da busca do cabeçalho (protótipo v2): recorta os cards por nome ou código. */
  busca?: string
  /** O que a tela põe na barra ao lado das abas e de "Filtros" — o link de gerenciar. */
  acoesDaBarra?: ReactNode
}

export function QuadroDoFunil({
  funis,
  funil,
  erroDoCatalogo,
  aoRecarregarOCatalogo,
  catalogoCarregando = false,
  aoTrocarDeFunil,
  aoCriarNegocio,
  busca = '',
  acoesDaBarra,
}: PropsDoQuadroDoFunil) {
  const ehCelular = useEhCelular()
  const { data: sessao } = useSessao()
  const perfil = sessao?.perfil

  /*
   * **O recorte de dinheiro é do serviço, e esta linha só decide o que desenhar.** A resposta a
   * uma SDR não carrega a chave `valor` em item nenhum (FR-62): não há número aqui para esconder.
   * O que o perfil decide é o texto que entra no lugar dele e a existência da soma por coluna —
   * um total calculado sobre `valor ?? 0` sairia zerado para a SDR, e um zero ao lado de três
   * cards seria a tela afirmando que aquela coluna não vale nada.
   *
   * **Ela não é a única guarda, e não pode ser.** Quem manda no card é a chave que chegou
   * (`valor` nulo, abaixo): perfil ausente — sessão ainda no ar, um perfil novo que o SPA
   * não conheça — cairia aqui no ramo do "vê dinheiro" e escreveria "sem orçamento" sobre um
   * campo que o serviço recortou. É a única confusão que esta tela não pode cometer.
   */
  const mostraDinheiro = perfil !== 'sdr'
  /* Os filtros (protótipo v2) são recorte de leitura sobre os cards já lidos: nenhum pedido novo.
     Trocar de Funil os zera — a Etapa e o Responsável escolhidos eram daquele Funil. O zerar é
     **derivado** (o estado guarda de qual Funil ele é), e não um efeito: um `setState` num efeito
     custaria um render a mais em toda abertura, sem nada para mostrar. */
  const [filtrosAbertos, setFiltrosAbertos] = useState(false)
  const funilAberto = funil?.id
  const [filtrosGuardados, setFiltrosGuardados] = useState<{
    funilId: string | undefined
    filtros: FiltrosDoQuadro
  }>({ funilId: undefined, filtros: SEM_FILTROS })
  const filtros = filtrosGuardados.funilId === funilAberto ? filtrosGuardados.filtros : SEM_FILTROS
  const setFiltros = useCallback(
    (novos: FiltrosDoQuadro) => {
      setFiltrosGuardados({ funilId: funilAberto, filtros: novos })
    },
    [funilAberto],
  )
  /* O toast do Copiar do card: uma mensagem por vez, que some sozinha. */
  const [toast, setToast] = useState<{ texto: string; vez: number } | null>(null)
  useEffect(() => {
    if (toast === null) {
      return
    }
    const temporizador = window.setTimeout(() => {
      setToast(null)
    }, DURACAO_DO_TOAST_EM_MS)
    return () => {
      window.clearTimeout(temporizador)
    }
  }, [toast])
  const copiarOId = useCallback((codigo: number, semEmpresa: boolean) => {
    void copiarParaAreaDeTransferencia(String(codigo)).then((deuCerto) => {
      setToast({
        texto: !deuCerto
          ? COPIA_DO_CARD_FALHOU
          : semEmpresa
            ? ID_COPIADO_SEM_EMPRESA
            : ID_COPIADO_DO_CARD,
        vez: Date.now(),
      })
    })
  }, [])
  const consulta = useNegociosDoFunil(funil?.id)
  const mover = useMoverCardDeEtapa(funil?.id)
  const abrirNegocio = useAbrirNegocio()
  const { mutate: moverMutate, reset: moverReset } = mover
  /* A recusa de um arrasto é deste Funil: trocar de Funil a apaga, em vez de ela ficar escrita
     acima de um quadro onde nada foi recusado. */
  useEffect(() => {
    moverReset()
  }, [funilAberto, moverReset])
  const moverOCard = useCallback(
    (negocioId: string, etapaId: string) => {
      moverMutate({ negocioId, etapaId })
    },
    [moverMutate],
  )
  const regua = useRegua()
  const equipe = useResponsaveis()

  const itens = useMemo(() => consulta.data ?? [], [consulta.data])

  /* Um pedido em lote para a tela inteira, e não um por card (AD-25). */
  const idsDeEmpresas = useMemo(
    () => [...new Set(itens.flatMap((item) => (item.empresa_id === null ? [] : [item.empresa_id])))],
    [itens],
  )
  const empresas = useNomesDeEmpresas(idsDeEmpresas)
  /* 13.20b2: o contato do Negócio sem Empresa, num lote só do `cadastro` (AD-25). */
  const idsDeContatos = useMemo(
    () => [
      ...new Set(
        itens.flatMap((item) =>
          item.empresa_id === null && item.pessoa_id !== null ? [item.pessoa_id] : [],
        ),
      ),
    ],
    [itens],
  )
  const contatos = useNomesDePessoas(idsDeContatos)

  const nomesDaEquipe = useMemo(
    () => new Map((equipe.data ?? []).map((pessoa) => [pessoa.id, pessoa.nome])),
    [equipe.data],
  )

  /* A Previsão (11.8): o catálogo só é pedido quando algum card tem previsão marcada. */
  const idsDePrevisao = useMemo(
    () => [
      ...new Set(
        itens.flatMap((item) =>
          item.previsao_de_fechamento_id == null ? [] : [item.previsao_de_fechamento_id],
        ),
      ),
    ],
    [itens],
  )
  const previsoes = useItensDePrevisao(idsDePrevisao, { habilitada: idsDePrevisao.length > 0 })
  const previsaoDoItem = useCallback(
    (id: string | null | undefined) => previsaoNoCard(previsoes, id),
    [previsoes],
  )

  /*
   * As Etapas vêm **do catálogo**, já ordenadas pelo serviço (`ordem, id`) — e não deduzidas dos
   * Negócios que voltaram. A diferença aparece na coluna vazia: uma Etapa sem card precisa
   * existir na tela, porque ela é para onde o Negócio vai ser movido.
   */
  const etapas = useMemo(() => funil?.etapas ?? [], [funil])
  const nomesDasEtapas = useMemo(
    () => new Map(etapas.map((etapa) => [etapa.id, etapa.nome])),
    [etapas],
  )

  const cards: CardDoQuadro[] = useMemo(
    () =>
      itens.map((item) => {
        const empresaId = item.empresa_id
        const semEmpresa = empresaId === null
        /* 13.20b2: sem Empresa, o nome é o do contato — pela mesma escada de causas, sobre o lote
           de Pessoas. */
        const nome =
          empresaId === null
            ? item.pessoa_id === null
              ? undefined
              : contatos.nomes.get(item.pessoa_id)
            : empresas.nomes.get(empresaId)
        const razaoSocial = empresaId === null ? undefined : empresas.razoesSociais.get(empresaId)
        const loteCarregando = semEmpresa ? contatos.carregando : empresas.carregando
        const loteFalhou = semEmpresa ? contatos.falhou : empresas.falhou
        /*
         * As causas, separadas. Só `ausente` — lote inteiro chegado e nome fora dele — é a
         * degradação documentada do FR-30; enquanto o lote viaja (`pendente`), ou se ele falhou
         * (`indisponivel`), o card não afirma nada sobre a ficha ser ou não de quem está olhando.
         *
         * A ordem das guardas é a das certezas: `carregando` antes de `falhou` porque um lote
         * ainda no ar pode trazer o nome, e um mapa incompleto não é resposta final nem quando
         * outro lote já falhou.
         */
        const origemDoTitulo: OrigemDoTitulo =
          nome !== undefined
            ? 'resolvido'
            : loteCarregando
              ? 'pendente'
              : loteFalhou
                ? 'indisponivel'
                : 'ausente'
        /*
         * O recorte, medido no **corpo** e não no perfil: `valor` ausente é o FR-62 em ação
         * (`response_model_exclude_unset` no serviço), e `null` é "sem orçamento". O perfil
         * entra por `mostraDinheiro` porque a soma da coluna precisa de uma decisão de tela,
         * mas ele não é o que decide se aquele card teve o número escondido.
         */
        const recortado = !mostraDinheiro || item.valor === undefined
        const orcamento = !recortado && item.valor !== null ? Number(item.valor) : null
        return {
          id: item.id,
          codigo: item.codigo,
          /*
           * O nome que não veio vira o **código** do Negócio — o identificador que a operação já
           * usa na boca (AD-12). O card não some e nada é inventado.
           *
           * **Sem `NUMERO`, e isso não é esquecimento.** `codigo` é identificador, e não
           * quantidade: formatado, o de número 4471 viraria "Negócio 4.471" no card e continuaria
           * "código 4471" na confirmação do formulário ao lado — dois textos para o mesmo dado, e
           * um deles impossível de colar numa busca. Só `dias_sem_contato` e dinheiro contam
           * coisas, e são eles que ganham separador.
           */
          titulo: nome ?? `Negócio ${String(item.codigo)}`,
          ...(razaoSocial === undefined ? {} : { razaoSocial }),
          origemDoTitulo,
          semEmpresa,
          responsavel: nomesDaEquipe.get(item.responsavel_id) ?? VAZIO,
          responsavelId: item.responsavel_id,
          /*
           * Três estados, e não dois. `valor` **ausente** é o recorte da SDR (a chave nem vem no
           * corpo, FR-62); `null` é "sem orçamento", que é estado legítimo de Negócio em
           * andamento. Colapsar os dois faria a SDR ler que nenhum Negócio da casa tem orçamento.
           */
          valor: recortado ? null : orcamento === null ? SEM_ORCAMENTO : MOEDA.format(orcamento),
          orcamento,
          dias: `${NUMERO.format(item.dias_sem_contato)}d`,
          sinal: sinalDaCor(item.cor),
          cor: item.cor,
          etapaId: item.etapa_id,
          previsao: previsaoDoItem(item.previsao_de_fechamento_id),
        }
      }),
    [
      itens,
      empresas.nomes,
      empresas.razoesSociais,
      empresas.carregando,
      empresas.falhou,
      contatos.nomes,
      contatos.carregando,
      contatos.falhou,
      nomesDaEquipe,
      mostraDinheiro,
      previsaoDoItem,
    ],
  )

  /*
   * Os cards agrupados por Etapa — **uma** passada, e não um `filter` por coluna. Os que não
   * casam com Etapa nenhuma do catálogo saem à parte, para a coluna dos órfãos: sumir com eles
   * seria esconder trabalho sem dizer, e eles apareceriam no celular de qualquer forma.
   */
  /* A busca e os filtros recortam **depois** de montar os cards: o quadro é o mesmo, e o que
     muda é o que se vê dele. */
  const termo = busca.trim().toLowerCase()
  const cardsVisiveis = useMemo(
    () => cards.filter((card) => passaNoRecorte(card, termo, filtros)),
    [cards, termo, filtros],
  )
  const responsaveisDoQuadro = useMemo(() => {
    const vistos = new Map<string, string>()
    for (const card of cards) {
      if (!vistos.has(card.responsavelId)) {
        vistos.set(card.responsavelId, card.responsavel)
      }
    }
    return [...vistos.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [cards])
  const ativos = filtrosAtivos(filtros)
  const { porEtapa, orfaos } = useMemo(() => {
    const mapa = new Map<string, CardDoQuadro[]>()
    const soltos: CardDoQuadro[] = []
    for (const card of cardsVisiveis) {
      if (!nomesDasEtapas.has(card.etapaId)) {
        soltos.push(card)
        continue
      }
      const coluna = mapa.get(card.etapaId)
      if (coluna === undefined) {
        mapa.set(card.etapaId, [card])
      } else {
        coluna.push(card)
      }
    }
    return { porEtapa: mapa, orfaos: soltos }
  }, [cardsVisiveis, nomesDasEtapas])

  /*
   * O `!isError` na frente importa: numa recusa `consulta.data` também é `undefined`, e sem ele o
   * erro seria lido como "ainda carregando" — a barra sumiria junto com o quadro, e quem tomou um
   * `500` ficaria sem nenhuma saída. É a mesma fórmula de `ListaDeEmpresas`.
   *
   * A recusa do **catálogo** entra pelo mesmo motivo, e é a metade que faltava: sem Funil não há
   * `funil_id` para pedir, a consulta do quadro não sai e `consulta.data` nunca deixa de ser
   * `undefined`. Sem ela na fórmula, `GET /funis` recusado prendia a tela em "carregando" para
   * sempre.
   */
  const houveRecusa = consulta.isError || erroDoCatalogo !== undefined
  /*
   * O catálogo **vazio** é o terceiro desfecho de `GET /funis`, e ele não é recusa nem espera.
   *
   * Uma instalação sem Funil nenhum responde `200` com `[]` — e o react-query nunca repete um
   * `200`. Sem esta linha, `funil` ficaria `undefined` para sempre, `carregando` seria verdadeiro
   * para sempre, e a tela ficaria em "Carregando o quadro…" sem erro e sem saída: exatamente o
   * modo de falha que `erroDoCatalogo` existe para fechar, entrando pela outra porta.
   *
   * Ele vem **antes** de `carregando` na fórmula porque é o que a torna falsa: enquanto o
   * catálogo está no ar, `funis` também está vazio, e só `catalogoCarregando` separa os dois.
   */
  const semCatalogo = !houveRecusa && !catalogoCarregando && funis.length === 0
  const carregando =
    !houveRecusa &&
    !semCatalogo &&
    (funil === undefined || consulta.isLoading || consulta.data === undefined)

  /*
   * A legenda só existe depois de a régua chegar, e nunca sobre um par de zeros: com um
   * `?? 0`, a tela escreveria "até 0 dias sem contato" e "sem faixa amarela nesta régua" por um
   * instante — duas frases que descrevem uma régua que instalação nenhuma tem. Um número que
   * ainda não foi lido não pode ser afirmado, e é a mesma regra do subtítulo de `/empresas`.
   */
  const faixas =
    regua.data === undefined
      ? null
      : descreverAsFaixas(regua.data.dias_para_amarelo, regua.data.dias_para_vermelho)

  return (
    <section className="quadro" aria-labelledby={ID_DO_TITULO}>
      {/* O nome da região continua para o leitor de tela; na tela o quadro não tem título nem
          caixa (protótipo v2): a barra e as colunas são o corpo da tela. */}
      <h2 className="quadro__titulo quadro__so-para-leitor" id={ID_DO_TITULO}>
        {NOME_DO_QUADRO}
      </h2>
      <div className="quadro__barra">
        <div className="quadro__ferramentas">
          {/* A pílula é **navegação**: ela escreve `?funil=` e mais nada. O seletor de Funil do
              formulário de criação é **campo** ("em que Funil este Negócio nasce"), e os dois
              escrevem a mesma coisa — por isso nunca discordam. */}
          <div className="quadro__pilula" role="group" aria-label={ROTULO_DA_PILULA}>
            {funis.map((umFunil) => (
              <button
                type="button"
                key={umFunil.id}
                className="quadro__aba"
                aria-current={umFunil.id === funil?.id ? 'true' : undefined}
                onClick={() => {
                  aoTrocarDeFunil(umFunil.id)
                }}
              >
                {umFunil.nome}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="quadro__botao quadro__botao--pequeno"
            aria-expanded={filtrosAbertos}
            aria-controls="quadro-filtros"
            onClick={() => {
              setFiltrosAbertos((aberto) => !aberto)
            }}
          >
            {filtrosAbertos ? ROTULO_DE_OCULTAR_FILTROS : ROTULO_DOS_FILTROS}
            {ativos > 0 ? ` · ${String(ativos)}` : ''}
          </button>
          {acoesDaBarra}
        </div>
        <div className="quadro__legenda-e-escopo">
          {/* A legenda sai de `descreverAsFaixas`, a mesma função pura do cartão de
              Administração: os textos das três faixas existem num lugar só, e a régua degenerada
              já é dita por ela em vez de virar um intervalo vazio. */}
          {faixas === null ? null : (
            <ul className="quadro__legenda" aria-label="Faixas da régua">
              {faixas.map((faixa) => (
                <li className="quadro__faixa" key={faixa.cor}>
                  <PontoDaCor cor={faixa.cor} />
                  <span className="quadro__faixa-rotulo">{faixa.titulo}</span>
                  <span className="quadro__so-para-leitor">{` (${faixa.intervalo})`}</span>
                </li>
              ))}
            </ul>
          )}
          {funil === undefined ? null : (
            <span className="quadro__escopo">{escopoDo(funil, sessao?.escopo_de_carteira)}</span>
          )}
          {ehCelular ? null : <span className="quadro__escopo">{`· ${DICA_DE_ARRASTO}`}</span>}
        </div>
      </div>
      {filtrosAbertos ? (
        <div
          className="quadro__filtros"
          role="group"
          aria-label={NOME_DOS_FILTROS}
          id="quadro-filtros"
        >
          <select
            className="quadro__filtro"
            aria-label="Responsável"
            data-ativo={filtros.responsavel !== '' || undefined}
            value={filtros.responsavel}
            onChange={(evento) => {
              setFiltros({ ...filtros, responsavel: evento.target.value })
            }}
          >
            <option value="">Responsável: todos</option>
            {responsaveisDoQuadro.map(([id, nome]) => (
              <option value={id} key={id}>
                {nome}
              </option>
            ))}
          </select>
          <select
            className="quadro__filtro"
            aria-label="Etapa"
            data-ativo={filtros.etapa !== '' || undefined}
            value={filtros.etapa}
            onChange={(evento) => {
              setFiltros({ ...filtros, etapa: evento.target.value })
            }}
          >
            <option value="">Etapa: todas</option>
            {etapas.map((etapa) => (
              <option value={etapa.id} key={etapa.id}>
                {etapa.nome}
              </option>
            ))}
          </select>
          <select
            className="quadro__filtro"
            aria-label="Sem contato"
            data-ativo={filtros.cor !== '' || undefined}
            value={filtros.cor}
            onChange={(evento) => {
              setFiltros({ ...filtros, cor: evento.target.value as FiltrosDoQuadro['cor'] })
            }}
          >
            <option value="">Sem contato: todos</option>
            {(faixas ?? []).map((faixa) => (
              <option value={faixa.cor} key={faixa.cor}>
                {faixa.titulo}
              </option>
            ))}
          </select>
          {mostraDinheiro ? (
            <select
              className="quadro__filtro"
              aria-label="Orçamento"
              data-ativo={filtros.orcamento !== '' || undefined}
              value={filtros.orcamento}
              onChange={(evento) => {
                setFiltros({
                  ...filtros,
                  orcamento: evento.target.value as FiltrosDoQuadro['orcamento'],
                })
              }}
            >
              <option value="">Orçamento: todos</option>
              <option value="com">Com orçamento</option>
              <option value="sem">Sem orçamento</option>
            </select>
          ) : null}
          {ativos > 0 ? (
            <button
              type="button"
              className="quadro__limpar"
              onClick={() => {
                setFiltros(SEM_FILTROS)
              }}
            >
              {LIMPAR_FILTROS}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="quadro__corpo">
        {erroDoCatalogo !== undefined ? (
          /* O catálogo é pré-condição: sem coluna não há quadro, e a recusa dele vem antes da
             do quadro porque a segunda nem chegou a acontecer. */
          <div className="quadro__recusa">
            <p className="quadro__erro" role="alert">
              {`${SEM_CATALOGO} ${mensagemDe(erroDoCatalogo)}`}
            </p>
            {aoRecarregarOCatalogo === undefined ? null : (
              <button type="button" className="quadro__botao" onClick={aoRecarregarOCatalogo}>
                {TENTAR_DE_NOVO}
              </button>
            )}
          </div>
        ) : consulta.isError ? (
          /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
          <div className="quadro__recusa">
            <p className="quadro__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
            <button
              type="button"
              className="quadro__botao"
              onClick={() => {
                void consulta.refetch()
              }}
            >
              {TENTAR_DE_NOVO}
            </button>
          </div>
        ) : semCatalogo ? (
          /* Catálogo vazio não é quadro vazio, e nem espera: não há coluna porque não há Funil,
             e a tela diz onde se cria o primeiro. */
          <p className="quadro__aviso">{SEM_FUNIS}</p>
        ) : carregando ? (
          <p className="quadro__aviso" role="status">
            {CARREGANDO}
          </p>
        ) : etapas.length === 0 ? (
          /* Funil sem Etapa não é quadro vazio: é quadro que ainda não tem colunas, e a
             diferença é o que a gestão precisa ler para saber o que fazer. */
          <p className="quadro__aviso">{SEM_ETAPAS}</p>
        ) : cards.length > 0 && cardsVisiveis.length === 0 ? (
          /* Há Negócios, mas nenhum passa no recorte: a saída é limpar, e não criar. */
          <div className="quadro__vazio">
            <p className="quadro__aviso" role="status">
              {FILTROS_SEM_RESULTADO}
            </p>
            {ativos > 0 ? (
              <button
                type="button"
                className="quadro__botao"
                onClick={() => {
                  setFiltros(SEM_FILTROS)
                }}
              >
                {LIMPAR_FILTROS}
              </button>
            ) : null}
          </div>
        ) : cards.length === 0 ? (
          <div className="quadro__vazio">
            <p className="quadro__aviso">{SEM_NEGOCIOS}</p>
            {aoCriarNegocio === undefined ? null : (
              <button
                type="button"
                className="quadro__botao quadro__botao--primario"
                onClick={aoCriarNegocio}
              >
                {ROTULO_DO_NOVO_NEGOCIO}
              </button>
            )}
          </div>
        ) : (
          <>
            {/* O aviso vem **junto** com os cards, e não no lugar deles: o quadro chegou, e o
                que falhou foi só o nome. Escondê-lo faria a tela inteira de códigos parecer o
                recorte de visibilidade do FR-30 em ação. */}
            {empresas.falhou ? (
              <p className="quadro__aviso quadro__aviso--recusa" role="alert">
                {NOMES_NAO_VIERAM}
              </p>
            ) : null}
            {mover.isError ? (
              <p className="quadro__erro quadro__erro--movimento" role="alert">
                {mensagemDe(mover.error)}
              </p>
            ) : null}
            {ehCelular ? (
              <CardsEmpilhados
                cards={cardsVisiveis}
                etapaDe={(etapaId) => {
                  const etapa = etapas.find((uma) => uma.id === etapaId)
                  return { nome: etapa?.nome ?? COLUNA_DOS_ORFAOS, cor: etapa?.cor }
                }}
              />
            ) : (
              <Colunas
                etapas={etapas}
                porEtapa={porEtapa}
                orfaos={orfaos}
                mostraDinheiro={mostraDinheiro}
                aoSoltar={moverOCard}
                aoCopiar={copiarOId}
                aoAbrir={abrirNegocio}
              />
            )}
          </>
        )}
      </div>
      {toast === null ? null : (
        <div className="quadro__toast" role="status" key={toast.vez}>
          {toast.texto}
        </div>
      )}
    </section>
  )
}
